import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { PageDetailHeader } from "../components/PageDetailHeader";
import { Container } from "../components/Container";
import { Card } from "../components/Card";
import { Button } from "../components/Button";
import { Form } from "../components/Form";
import { FormActions } from "../components/FormActions";
import { InfoBox } from "../components/InfoBox";
import { CsvFileUpload } from "./components/productImport/CsvFileUpload";
import { GiftcardColumnMapping } from "./components/giftcardImport/GiftcardColumnMapping";
import { ImportOptions } from "./components/productImport/ImportOptions";
import { GiftcardDryRunResults } from "./components/giftcardImport/GiftcardDryRunResults";
import { ImportDropZone } from "./components/productImport/ImportDropZone";
import { analyzeCsvFile, parseCSVHeaders } from "./components/productImport/csvUtils";
import { useImportGiftcardsMutation } from "../query/giftcardsQuery";
import type { StoreId } from "@KassaroAB/shared/types/ids";
import type {
  ImportGiftcardsDryRunResults,
  ImportGiftcardsResult,
} from "../api/giftcardsApi";
import { ProductImportSection } from "./components/productImport/ProductImportSection";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";

const AlertsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
  margin-bottom: ${spacing.x3}px;
`;

const DryRunAlertsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
  margin-bottom: ${spacing.x3}px;
`;

const MapContinueHint = styled.p`
  margin: ${spacing.x3}px 0 0 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${colors.textMuted};
`;

const isDryRunResponse = (data: unknown): data is ImportGiftcardsDryRunResults => {
  return (
    typeof data === "object" &&
    data !== null &&
    "success" in data &&
    "totalRows" in data &&
    !("created" in data)
  );
};

const isImportResponse = (data: unknown): data is ImportGiftcardsResult => {
  return (
    typeof data === "object" &&
    data !== null &&
    "created" in data &&
    "updated" in data
  );
};

export const GiftcardImportView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { storeId } = useParams<{ storeId: string }>();
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [columnMappings, setColumnMappings] = useState<Record<number, string>>({});
  const [skipFirstRow, setSkipFirstRow] = useState(true);
  const [encoding, setEncoding] = useState("utf-8");
  const [delimiter, setDelimiter] = useState(",");
  const [onlyUpdate, setOnlyUpdate] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [dryRunError, setDryRunError] = useState<string | null>(null);
  const [dryRunSuccess, setDryRunSuccess] = useState<string | null>(null);
  const [dryRunCompleted, setDryRunCompleted] = useState(false);
  const [dryRunResults, setDryRunResults] = useState<ImportGiftcardsDryRunResults | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  const { mutate: importGiftcards, isPending: isImportPending } =
    useImportGiftcardsMutation((storeId as StoreId) || null);

  const step1Complete = Boolean(csvFile && csvHeaders.length > 0);
  const step2Complete =
    step1Complete &&
    csvHeaders.some((_, i) => {
      const v = columnMappings[i];
      return typeof v === "string" && v.length > 0;
    });

  const resetDryRunState = () => {
    setDryRunCompleted(false);
    setDryRunResults(null);
    setDryRunError(null);
    setDryRunSuccess(null);
  };

  const handleFileChange = (file: File | null) => {
    if (!file) {
      setCsvFile(null);
      setCsvHeaders([]);
      setColumnMappings({});
      setParseError(null);
      resetDryRunState();
      return;
    }

    setCsvFile(file);
    setParseError(null);
    resetDryRunState();

    analyzeCsvFile(file)
      .then(({ headers, encoding: detectedEncoding, delimiter: detectedDelimiter }) => {
        setEncoding(detectedEncoding);
        setDelimiter(detectedDelimiter);
        setCsvHeaders(headers);
        const initialMappings: Record<number, string> = {};
        headers.forEach((_, index) => {
          initialMappings[index] = "";
        });
        setColumnMappings(initialMappings);
      })
      .catch((err: Error) => {
        setParseError(t("giftcardImport.parseCsvFailed", { message: err.message }));
        setCsvHeaders([]);
      });
  };

  const handleEncodingChange = (newEncoding: string) => {
    setEncoding(newEncoding);
    resetDryRunState();
    if (csvFile) {
      parseCSVHeaders(csvFile, newEncoding, delimiter)
        .then((headers) => {
          setCsvHeaders(headers);
          const initialMappings: Record<number, string> = {};
          headers.forEach((_, index) => {
            initialMappings[index] = columnMappings[index] || "";
          });
          setColumnMappings(initialMappings);
        })
        .catch((err: Error) => {
          setParseError(t("giftcardImport.parseCsvFailed", { message: err.message }));
        });
    }
  };

  const handleDelimiterChange = (newDelimiter: string) => {
    setDelimiter(newDelimiter);
    resetDryRunState();
    if (csvFile) {
      parseCSVHeaders(csvFile, encoding, newDelimiter)
        .then((headers) => {
          setCsvHeaders(headers);
          const initialMappings: Record<number, string> = {};
          headers.forEach((_, index) => {
            initialMappings[index] = columnMappings[index] || "";
          });
          setColumnMappings(initialMappings);
        })
        .catch((err: Error) => {
          setParseError(t("giftcardImport.parseCsvFailed", { message: err.message }));
        });
    }
  };

  const handleMappingChange = (index: number, value: string) => {
    setColumnMappings((prev) => ({
      ...prev,
      [index]: value,
    }));
    resetDryRunState();
  };

  const buildImportParams = () => {
    const mappingArray = csvHeaders.map((_, index) => {
      const mapping = columnMappings[index] || "SKIP";
      return mapping === "" ? "SKIP" : mapping;
    });

    return {
      columnMapping: mappingArray,
      csvFile: csvFile!,
      defaultValues: {},
      skipFirstRow,
      encoding,
      delimiter,
      onlyUpdate,
    };
  };

  const handleDryRun = (e: React.FormEvent) => {
    e.preventDefault();
    setDryRunError(null);
    setDryRunSuccess(null);
    setSuccessMessage(null);
    setDryRunResults(null);

    if (!csvFile) {
      setDryRunError(t("giftcardImport.selectCsvFile"));
      return;
    }

    if (csvHeaders.length === 0) {
      setDryRunError(t("giftcardImport.noCsvHeaders"));
      return;
    }

    importGiftcards(
        {
          ...buildImportParams(),
          dryrun: true,
        },
        {
          onSuccess: (data) => {
            if (!data) {
              setDryRunError(t("giftcardImport.failedRunDryRun"));
              return;
            }
            if (isDryRunResponse(data)) {
              setDryRunResults(data);
              setDryRunCompleted(true);
              setDryRunSuccess(t("giftcardImport.dryRunCompletedReview"));
            }
          },
          onError: (err: Error) => {
            setDryRunError(err.message || t("giftcardImport.dryRunFailed"));
            setDryRunCompleted(false);
            setDryRunResults(null);
          },
        },
      );
  };

  const handleImport = () => {
    setError(null);
    setSuccessMessage(null);
    setIsImporting(true);

    if (!csvFile) {
      setError(t("giftcardImport.selectCsvFile"));
      setIsImporting(false);
      return;
    }

    if (csvHeaders.length === 0) {
      setError(t("giftcardImport.noCsvHeaders"));
      setIsImporting(false);
      return;
    }

    if (!dryRunCompleted) {
      setError(t("giftcardImport.runDryRunFirst"));
      setIsImporting(false);
      return;
    }

    importGiftcards(
        {
          ...buildImportParams(),
          dryrun: false,
        },
        {
          onSuccess: (data) => {
            if (data && isImportResponse(data)) {
              if (storeId) {
                queryClient.invalidateQueries({
                  queryKey: ["stores", storeId, "giftCards"],
                });
              }
              setSuccessMessage(
                t("giftcardImport.importSuccessSummary", {
                  created: data.created,
                  updated: data.updated,
                  skipped: data.skipped,
                  failed: data.failed,
                }),
              );
              setCsvFile(null);
              setCsvHeaders([]);
              setColumnMappings({});
              resetDryRunState();
            }
            setIsImporting(false);
          },
          onError: (err: Error) => {
            setError(err.message || t("giftcardImport.importFailed"));
            setIsImporting(false);
          },
        },
      );
  };

  return (
    <Container>
      <PageDetailHeader
        title={t("giftcardImport.title")}
        parentLabel={t("giftcardsView.title")}
        parentTo={`/stores/${storeId}/giftcards`}
        currentLabel={t("giftcardImport.title")}
      />
      <ImportDropZone onFile={(file) => handleFileChange(file)}>
        <Card>
          {(parseError || error || successMessage) && (
            <AlertsStack>
              {parseError && <InfoBox variant="error">{parseError}</InfoBox>}
              {error && <InfoBox variant="error">{error}</InfoBox>}
              {successMessage && <InfoBox variant="success">{successMessage}</InfoBox>}
            </AlertsStack>
          )}

          <Form onSubmit={(e) => e.preventDefault()}>
            <ProductImportSection
              title={t("giftcardImport.sectionUploadTitle")}
              description={[
                t("giftcardImport.sectionUploadDescription"),
                t("giftcardImport.sectionMatchExplanation"),
              ]}
            >
              <CsvFileUpload file={csvFile} onFileChange={handleFileChange} />
              <ImportOptions
                skipFirstRow={skipFirstRow}
                encoding={encoding}
                delimiter={delimiter}
                onlyUpdate={onlyUpdate}
                onSkipFirstRowChange={(value) => {
                  setSkipFirstRow(value);
                  resetDryRunState();
                }}
                onEncodingChange={handleEncodingChange}
                onDelimiterChange={handleDelimiterChange}
                onOnlyUpdateChange={(value) => {
                  setOnlyUpdate(value);
                  resetDryRunState();
                }}
                entityImportNs="giftcardImport"
              />
            </ProductImportSection>

            {step1Complete && (
              <ProductImportSection
                title={t("giftcardImport.sectionMappingTitle")}
                description={t("giftcardImport.sectionMappingDescription")}
              >
                <GiftcardColumnMapping
                  headers={csvHeaders}
                  mappings={columnMappings}
                  onMappingChange={handleMappingChange}
                />
                {!step2Complete && (
                  <MapContinueHint>{t("giftcardImport.mapAtLeastOneHint")}</MapContinueHint>
                )}
              </ProductImportSection>
            )}

            {step2Complete && (
              <ProductImportSection
                title={t("giftcardImport.sectionActionsTitle")}
                description={t("giftcardImport.sectionActionsDescription")}
              >
                {(dryRunError || dryRunSuccess) && (
                  <DryRunAlertsStack>
                    {dryRunError && <InfoBox variant="error">{dryRunError}</InfoBox>}
                    {dryRunSuccess && <InfoBox variant="success">{dryRunSuccess}</InfoBox>}
                  </DryRunAlertsStack>
                )}
                <FormActions>
                  <Button type="button" $color="secondary" onClick={() => navigate(-1)}>
                    {t("common.cancel")}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleDryRun}
                    disabled={isImportPending || !csvFile || csvHeaders.length === 0}
                  >
                    {isImportPending ? t("giftcardImport.running") : t("giftcardImport.runDryRun")}
                  </Button>
                </FormActions>
              </ProductImportSection>
            )}
          </Form>

          {step2Complete && dryRunResults !== null && (
            <GiftcardDryRunResults
              results={dryRunResults}
              onImport={handleImport}
              canImport={dryRunCompleted && dryRunResults.success}
              isImporting={isImporting || isImportPending}
            />
          )}
        </Card>
      </ImportDropZone>
    </Container>
  );
};
